/* eslint-disable camelcase */
import React from "react";
import { ReactComponent as ExportIcon } from "./../../images/svg/export.svg";
import { ReactComponent as ArrowUpIcon } from "./../../images/svg/arrowUp.svg";
import { ReactComponent as ArrowDownIcon } from "./../../images/svg/arrowDown.svg";
import { ReactComponent as LoaderIcon } from "./../../images/svg/loader.svg";
import contains from "lodash/includes";
import keys from "lodash/keys";
import each from "lodash/each";
import pick from "lodash/pickBy";
import findLastIndex from "lodash/findLastIndex";
import difference from "lodash/difference";
import { d3, gettext } from "../../adapter";
import { cropr } from "../../adapter";
import moment from "moment";
import { export2Csv } from "../../utils/sensorUtils";
import { connect, shallowEqual } from "react-redux";
import IrrigationSumChart from "../../charts/irrigationSumChart";
import IrrigationDepthChart from "../../charts/irrigationDepthChart";
import sensorActions, { fetchSensor } from "../../actions/sensorActions";

class IrrigationCharts extends React.Component {
  constructor(props) {
    super(props);

    this.chartRef = React.createRef();
    this.chartBrushRef = React.createRef();
    this.chartDepthRef = React.createRef();
    this.chartDepthRef.current = {};

    this.chart = undefined;
    this.chartBrush = undefined;
    this.chartDepths = [];

    this.visibleDepths = keys(
      pick(props.sensor.depths, function (value, key, object) {
        return value.visible;
      })
    );
    this.totalDepths = keys(props.sensor.depths);

    this.state = { exportOpen: false };
  }

  handleResize(event) {
    var _this = this;
    var el = this.chartRef.current;
    var elBrush = this.chartBrushRef.current;

    if (el) this.chart.resize(parseInt(d3.select(el).style("width"), 10), false);
    if (elBrush) this.chartBrush.resize(parseInt(d3.select(elBrush).style("width"), 10), false);

    each(this.chartDepths, function (chart) {
      var elDepth = _this.chartDepthRef.current[chart.options.depth];
      if (elDepth) chart.resize(parseInt(d3.select(elDepth).style("width"), 10), false);
    });
  }

  componentDidUpdate(prevProps, prevState) {
    var _this = this;
    if (this.props.showAllDepths !== prevState.showAllDepths) {
      var depthDifference = difference(this.totalDepths, this.visibleDepths);
      if (this.props.showAllDepths) {
        each(depthDifference, function (depth) {
          _this.renderDepthChart(depth);
        });
      } else {
        each(depthDifference, function (depth) {
          _this.destroyDepthChart(depth);
        });
      }
    }
  }

  destroyDepthChart(depth) {
    var index = findLastIndex(this.chartDepths, function (chart) {
      return chart.options.depth === depth;
    });

    if (index > -1) {
      this.chartDepths.splice(index, 1);
    }
    // Destroy the chart in d3?
  }

  componentDidMount() {
    var _this = this;
    var el = this.chartRef.current;
    var elBrush = this.chartBrushRef.current;
    var sensor = this.props.sensor;
    var hasPrecipitation = false;
    var hasTemperature = false;

    var height = 300;
    if (sensor.showPrecipitation && contains(sensor.sensors, "precipitation")) {
      hasPrecipitation = true;
      height = 400;
    }

    if (sensor.showTemperature && contains(sensor.sensors, "soil_temperature")) {
      hasTemperature = true;
      height = 500;
    }

    // Load sumchart
    this.chart = new IrrigationSumChart(
      {
        id: "chart-water-sum",
        height: height,
        class: "chart water-sum",
        hasPrecipitation: hasPrecipitation,
        hasTemperature: hasTemperature,
        width: parseInt(d3.select(el).style("width"), 10), // With of the container element
      },
      this.getChartState(this.props),
      this.props.dispatch
    );

    this.chart.init(el);

    // Load brushchart
    this.chartBrush = new IrrigationSumChart(
      {
        id: "chart-water-sum-brush",
        isBrush: true,
        height: 150,
        class: "chart water-sum brush",
        width: parseInt(d3.select(elBrush).style("width"), 10), // With of the container element
      },
      this.getChartState(this.props),
      this.props.dispatch
    );
    this.chartBrush.init(elBrush);

    // Load depth charts
    each(this.props.showAllDepths ? this.totalDepths : this.visibleDepths, function (depth) {
      _this.renderDepthChart(depth);
    });
    window.addEventListener("resize", () => {
      this.handleResize();
    });
  }

  renderDepthChart(depth) {
    var sensor = this.props.sensor;

    var height = 200;
    if (sensor.showTemperature && sensor.measurements.temperature) {
      var hasTemperature = true;
      height = 275;
    }

    var elDepth = this.chartDepthRef.current[depth];
    var visible = true;
    var className = "chart water-depth";
    var chart = new IrrigationDepthChart(
      {
        id: "chart-water-" + depth,
        height: height,
        depth: depth,
        hasTemperature: hasTemperature,
        class: className,
        visible: visible,
        width: parseInt(d3.select(elDepth).style("width"), 10), // With of the container element
      },
      this.getChartState(this.props),
      this.props.dispatch
    );
    chart.init(elDepth);
    this.chartDepths.push(chart);
  }

  getChartState(props) {
    return {
      sensor: props.sensor,
      lastDataPoint: props.lastDataPoint,
      depths: props.sensor.getVisibleDepths(),
      domain: props.domain,
      hoverDate: props.hoverDate,
      pinDate: props.pinDate,
      pinned: props.pinned,
    };
  }

  componentWillUnmount() {
    this.chartBrush.destroy();
    this.chart.destroy();
    each(this.chartDepths, function (chart) {
      chart.destroy();
    });
  }

  reloadData(days, setup, setup_revert) {
    if (this.props.sensor.id) {
      this.props.dispatch(
        fetchSensor(this.props.apiUrl, this.props.sensor.id, null, true, days, setup, setup_revert)
      );
    }
  }

  shouldComponentUpdate(nextProps) {
    var _this = this;

    if (
      !shallowEqual(this.props.domain, nextProps.domain) ||
      !shallowEqual(this.props.sensor, nextProps.sensor)
    ) {
      this.chart.update(this.getChartState(nextProps));
      this.chartBrush.update(this.getChartState(nextProps));

      each(this.chartDepths, function (chart) {
        chart.update(_this.getChartState(nextProps));
      });
    }

    // Change hovered state
    if (this.props.hovered !== nextProps.hovered || this.props.pinned !== nextProps.pinned) {
      this.chartBrush.updateHoverState(nextProps.hovered, nextProps.pinned, nextProps.pinDate);
      this.chart.updateHoverState(nextProps.hovered, nextProps.pinned, nextProps.pinDate);
      each(this.chartDepths, function (chart) {
        chart.updateHoverState(nextProps.hovered, nextProps.pinned, nextProps.pinDate);
      });

      return false;
    }

    // Change the hover date
    this.chart.updateHoverDate(nextProps.hoverDate, nextProps.pinned, nextProps.pinDate);
    each(this.chartDepths, function (chart) {
      chart.updateHoverDate(nextProps.hoverDate, nextProps.pinned, nextProps.pinDate);
    });

    // Don't update on hover
    return this.props.hoverDate === nextProps.hoverDate;
  }

  render() {
    const { sensor, domain, loading } = this.props;
    const _this = this;

    var preloaderClass = "object-preloader is-loaded";
    if (loading) {
      preloaderClass = "object-preloader is-loading";
    }

    var from = domain[0];
    var to = domain[1];

    var togglePrecipitationSum = "";

    var totalPrecipitationInPeriod = -1;
    if (sensor.showPrecipitation && contains(sensor.sensors, "precipitation")) {
      var totalPrecipitationInPeriodArray = sensor.measurements.precipitation.filter(function (el) {
        return el.timestamp >= from && el.timestamp <= to;
      });

      totalPrecipitationInPeriod = d3.sum(totalPrecipitationInPeriodArray, function (d) {
        return d.y;
      });
      var precision = cropr.l10n.unit_precipitation === "in" ? 1 : 0;
      togglePrecipitationSum = (
        <span>
          <span className="precipitation">
            <span className="precipitation1"></span>
            <span className="precipitation2"></span>
            <span className="precipitation3"></span>
          </span>
          <h2>
            <small>
              {gettext("Precipitation sum: {{sum}} {{unit}}")
                .replace("{{sum}}", cropr.toLocaleFixed(totalPrecipitationInPeriod, precision))
                .replace("{{unit}}", cropr.l10n.unit_precipitation_display)}
            </small>
            &nbsp;
          </h2>
        </span>
      );
    }

    var startDate = "-";
    if (sensor.startDate) {
      startDate = moment(sensor.startDate.toDate()).format("dd, LL");
    }

    var startDateMessage = "";
    if (sensor.startDate && sensor.allmeasurements && sensor.allmeasurements.length > 0) {
      var firstmeasurement = new Date(sensor.allmeasurements[0].timestamp * 1000);
      if ((firstmeasurement - sensor.startDate.toDate()) / 1000 > 100000) {
        startDateMessage = " (" + moment(firstmeasurement).format("dd, LL") + ") ";
      }
    }

    var updateSensor = "";
    var revertSensor = "";
    if (
      (sensor.role === "owner" || sensor.role === "editor") &&
      cropr.permissions.farm.update === true
    ) {
      updateSensor = (
        // eslint-disable-next-line
        <a
          href="#"
          className="button is-right"
          onClick={() => {
            this.reloadData("", true, true);
          }}
        >
          {gettext("Undo")}
        </a>
      );
      revertSensor = (
        // eslint-disable-next-line
        <a
          href="#"
          className="button is-right"
          onClick={() => {
            this.reloadData("", true);
          }}
        >
          {gettext("Auto Setup")}
        </a>
      );
    }

    var endDate = gettext("Now");
    if (sensor.endDate && moment().isAfter(sensor.endDate)) {
      endDate = moment(sensor.endDate.toDate()).format("dd, LL");
    }

    var endDateMessage = "";
    if (moment().isBefore(sensor.endDate)) {
      endDateMessage = "(Eindatum: " + moment(sensor.endDate.toDate()).format("dd, LL") + ")";
    }

    var depthsToShow = this.props.showAllDepths ? this.totalDepths : this.visibleDepths;
    var depths = depthsToShow.map(function (depth) {
      return (
        <div
          className="l-half no-margin"
          ref={(el) => (_this.chartDepthRef.current[depth] = el)}
          key={depth}
        />
      );
    });

    var toggleDepths = "";

    var exportClass = "panel-options__item dropdown__wrapper";
    if (this.state.exportOpen) {
      exportClass = "panel-options__item dropdown__wrapper is-open";
    }

    // If visibledepths and totalDepths are equal, don't show thore more/less links
    if (this.visibleDepths.length !== this.totalDepths.length) {
      if (this.props.showAllDepths) {
        toggleDepths = (
          <div className="showmore-less text-center">
            {/* eslint-disable-next-line */}
            <a
              className="button--neutral"
              onClick={() => {
                this.props.dispatch(sensorActions.toggleVisibleDepths());
              }}
            >
              {gettext("Show less depths")}
              <ArrowUpIcon />
            </a>
          </div>
        );
      } else {
        toggleDepths = (
          <div className="showmore-less text-center">
            {/* eslint-disable-next-line */}
            <a
              className="button--neutral"
              onClick={() => {
                this.props.dispatch(sensorActions.toggleVisibleDepths());
              }}
            >
              {gettext("Show all depths")}
              <ArrowDownIcon />
            </a>
          </div>
        );
      }
    }

    return (
      <div>
        <div className="l-full">
          <div className="panel has-object-preloader">
            <div className={preloaderClass}>
              <div className="object-preloader--content">
                <LoaderIcon className="loader" />
              </div>
            </div>
            <h2>
              {sensor.type} {gettext("date range filter")}
              <small>
                {startDate} {startDateMessage} — {endDate} {endDateMessage}
              </small>
              <small className="right">{sensor.advice_region}</small>
            </h2>
            <div ref={this.chartBrushRef} />
          </div>
        </div>

        <div className="l-full">
          <div className="panel">
            <div className="panel--header">
              <h2>
                {gettext("Progress soil moisture")}
                <small>
                  {moment(from).format("dd, LL")} — {moment(to).format("dd, LL")}
                </small>
              </h2>

              <ul className="panel-options">
                <li className="panel-options__item">{togglePrecipitationSum}</li>
                <li
                  className={exportClass}
                  onClick={() => {
                    this.setState({ exportOpen: !this.state.exportOpen });
                  }}
                >
                  {/* eslint-disable-next-line */}
                  <a className="dropdown--toggle">
                    <ExportIcon className="icon" /> {gettext("Export")}
                  </a>
                  <div className="dropdown">
                    <ul className="dropdown__list">
                      <li>
                        {/* eslint-disable-next-line */}
                        <a
                          onClick={() => {
                            export2Csv(sensor, domain);
                          }}
                        >
                          {gettext("To CSV")}
                        </a>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
            <div ref={this.chartRef} />
          </div>
        </div>

        <div className="l-full">
          <div className="panel">
            <div className="panel--header">
              <div className="buttons--header">
                <h2>
                  {gettext("Depth analysis")}
                  <small>
                    {moment(from).format("dd, LL")} — {moment(to).format("dd, LL")}
                  </small>
                </h2>
                {updateSensor}
                {revertSensor}
              </div>
            </div>

            {depths}
            {toggleDepths}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ ...state.sensorAnalysis });

export default connect(mapStateToProps)(IrrigationCharts);
